import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assessSize,
  createPdfEngine,
  openThumbnails,
  PdfEngineError,
  type PagePlan,
  type PdfEngine,
  type PdfInfo,
  type PdfThumbnails,
  type PlacedImage,
} from '@/tools/pdf-engine';
import { normaliseTurn, type QuarterTurn } from './placement-math';

export interface OpenFile {
  readonly id: number;
  readonly name: string;
  readonly bytes: number;
  readonly info: PdfInfo;
  readonly thumbs: Promise<PdfThumbnails>;
}

export interface PageItem {
  /** Stable across reorders, for React keys and the selection. */
  readonly key: string;
  readonly source: number;
  readonly page: number;
  /** Our extra clockwise turn on top of the page's own. */
  readonly rotate: QuarterTurn;
  readonly images: readonly PlacedImage[];
}

export type Notice =
  | { readonly kind: 'password-required' | 'not-a-pdf'; readonly name: string }
  | { readonly kind: 'failed' };

let keySeq = 0;

/** Opens one file in the engine: its entry and its pages, one item each, in order. */
async function openFile(
  engine: PdfEngine,
  file: File,
): Promise<{ entry: OpenFile; pages: PageItem[] }> {
  const opened = await engine.open(file, file.name);
  const thumbs = openThumbnails(file);
  thumbs.catch(() => undefined);
  const entry: OpenFile = {
    id: opened.id,
    name: file.name,
    bytes: file.size,
    info: opened.info,
    thumbs,
  };
  const pages: PageItem[] = [];
  for (let page = 0; page < opened.info.pages; page++) {
    pages.push({ key: `p${++keySeq}`, source: opened.id, page, rotate: 0, images: [] });
  }
  return { entry, pages };
}

function noticeFor(error: unknown, name: string): Notice {
  const code = error instanceof PdfEngineError ? error.code : 'failed';
  return code === 'password-required' || code === 'not-a-pdf'
    ? { kind: code, name }
    : { kind: 'failed' };
}

function nextSelection(
  current: ReadonlySet<string>,
  key: string,
  how: 'only' | 'toggle' | 'range',
  pages: readonly PageItem[],
): ReadonlySet<string> {
  if (how === 'toggle') {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }
  if (how === 'range' && current.size > 0) {
    const keys = pages.map((p) => p.key);
    const anchor = keys.findIndex((k) => current.has(k));
    const target = keys.indexOf(key);
    const [from, to] = anchor < target ? [anchor, target] : [target, anchor];
    return new Set(keys.slice(from, to + 1));
  }
  return new Set([key]);
}

function reorder(current: readonly PageItem[], from: number, to: number): readonly PageItem[] {
  if (from === to || from < 0 || from >= current.length) return current;
  const next = [...current];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item!);
  return next;
}

/** The selected pages leave and `added` takes the place of the first of them. */
function replacePages(
  current: readonly PageItem[],
  selected: ReadonlySet<string>,
  added: readonly PageItem[],
): readonly PageItem[] {
  const at = current.findIndex((p) => selected.has(p.key));
  // The selection went away while the file opened: add the pages at the end instead.
  if (at < 0) return [...current, ...added];
  const kept = current.filter((p) => !selected.has(p.key));
  const index = current.slice(0, at).filter((p) => !selected.has(p.key)).length;
  return [...kept.slice(0, index), ...added, ...kept.slice(index)];
}

const toPlan = (items: readonly PageItem[]): PagePlan[] =>
  items.map((p) => ({ source: p.source, page: p.page, rotate: p.rotate, images: p.images }));

/** The open files, the engine behind them and the notices and busy flag of the work on them. */
function usePdfFiles(pages: readonly PageItem[]) {
  const engineRef = useRef<PdfEngine | null>(null);
  const filesRef = useRef<OpenFile[]>([]);
  const [files, setFiles] = useState<readonly OpenFile[]>([]);
  const [notices, setNotices] = useState<readonly Notice[]>([]);
  const [busy, setBusy] = useState(false);

  const engine = useCallback((): PdfEngine => (engineRef.current ??= createPdfEngine()), []);

  useEffect(
    () => () => {
      engineRef.current?.dispose();
      for (const f of filesRef.current)
        void f.thumbs.then((t) => t.destroy()).catch(() => undefined);
    },
    [],
  );

  /** Opens `list`; returns the pages of the files that opened, in order. */
  const open = useCallback(
    async (list: readonly File[]): Promise<PageItem[]> => {
      const added: PageItem[] = [];
      const problems: Notice[] = [];
      for (const file of list) {
        try {
          const opened = await openFile(engine(), file);
          filesRef.current = [...filesRef.current, opened.entry];
          setFiles(filesRef.current);
          added.push(...opened.pages);
        } catch (error) {
          problems.push(noticeFor(error, file.name));
        }
      }
      setNotices(problems);
      return added;
    },
    [engine],
  );

  const build = useCallback(
    async (items: readonly PageItem[]): Promise<Blob | null> => {
      if (items.length === 0) return null;
      setNotices([]);
      setBusy(true);
      try {
        return await engine().assemble(toPlan(items));
      } catch {
        setNotices([{ kind: 'failed' }]);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [engine],
  );

  // A file none of the pages uses any more (all deleted or replaced) is closed and forgotten, so it
  // no longer counts for the size warning, the name or the notices, nor holds memory.
  useEffect(() => {
    const used = new Set(pages.map((p) => p.source));
    const unused = filesRef.current.filter((f) => !used.has(f.id));
    if (unused.length === 0 || pages.length === 0) return;
    for (const f of unused) {
      engineRef.current?.close(f.id);
      void f.thumbs.then((t) => t.destroy()).catch(() => undefined);
    }
    filesRef.current = filesRef.current.filter((f) => used.has(f.id));
    setFiles(filesRef.current);
  }, [pages]);

  return { files, notices, setNotices, busy, setBusy, open, build };
}

function useSelection(pages: readonly PageItem[]) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const select = useCallback(
    (key: string, how: 'only' | 'toggle' | 'range'): void => {
      setSelected((current) => nextSelection(current, key, how, pages));
    },
    [pages],
  );
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  return { selected, setSelected, select, clearSelection };
}

/**
 * The PDF workspace's state (#387, decision 98): the open files, their pages as one ordered list,
 * the selection, and the actions. Everything goes through the PDF engine on the device.
 */
export function usePdfWorkspace() {
  const [pages, setPages] = useState<readonly PageItem[]>([]);
  const { files, notices, setNotices, busy, setBusy, open, build } = usePdfFiles(pages);
  const { selected, setSelected, select, clearSelection } = useSelection(pages);

  const addFiles = useCallback(
    async (list: readonly File[]): Promise<void> => {
      setNotices([]);
      setBusy(true);
      try {
        const added = await open(list);
        setPages((current) => [...current, ...added]);
      } finally {
        setBusy(false);
      }
    },
    [open],
  );

  const move = useCallback((from: number, to: number): void => {
    setPages((current) => reorder(current, from, to));
  }, []);

  const rotateSelected = useCallback((): void => {
    setPages((current) =>
      current.map((p) =>
        selected.has(p.key) ? { ...p, rotate: normaliseTurn(p.rotate + 90) } : p,
      ),
    );
  }, [selected]);

  const deleteSelected = useCallback((): void => {
    setPages((current) => current.filter((p) => !selected.has(p.key)));
    setSelected(new Set());
  }, [selected]);

  const replaceSelected = useCallback(
    async (file: File): Promise<void> => {
      if (selected.size === 0) return;
      setBusy(true);
      try {
        const added = await open([file]);
        if (added.length === 0) return;
        setPages((current) => replacePages(current, selected, added));
        setSelected(new Set(added.map((p) => p.key)));
      } finally {
        setBusy(false);
      }
    },
    [open, selected],
  );

  const placeImage = useCallback((key: string, image: PlacedImage): void => {
    setPages((current) =>
      current.map((p) => (p.key === key ? { ...p, images: [...p.images, image] } : p)),
    );
  }, []);

  const totalBytes = files.reduce((sum, f) => sum + f.bytes, 0);

  return {
    files,
    pages,
    selected,
    notices,
    busy,
    size: assessSize(totalBytes, pages.length),
    addFiles,
    select,
    clearSelection,
    move,
    rotateSelected,
    deleteSelected,
    replaceSelected,
    placeImage,
    build,
    fileOf: (id: number) => files.find((f) => f.id === id),
  };
}

/** Saves `blob` as `name` through a temporary link. */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * The output name: the first file's name, a suffix and the local time, e.g.
 * contratto-modificato-1904.pdf. The time keeps each download's name new, so a phone browser does
 * not ask whether to download the file again (the Director, 2026-10-07).
 */
export function outputName(
  files: readonly OpenFile[],
  suffix: string,
  now: Date = new Date(),
): string {
  const base = (files[0]?.name ?? 'documento').replace(/\.pdf$/i, '');
  const hhmm = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  return `${base}-${suffix}-${hhmm}.pdf`;
}
